import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { logger } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session lives in an httpOnly cookie set by the server; nothing sensitive is persisted client-side.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const applySession = (data, setToken, setUser) => {
  const { token: newToken, user: userData } = data;
  setToken(newToken || null);
  setUser(userData);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // in-memory only (legacy Bearer callers); never stored
  const [loading, setLoading] = useState(true);
  const sessionProbe = useRef(null);
  const authEpoch = useRef(0);
  const signedOut = useRef(false);
  const pendingLogout = useRef(null);

  const logout = useCallback(async () => {
    signedOut.current = true;
    authEpoch.current += 1;
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    if (!pendingLogout.current) {
      pendingLogout.current = axios.post(`${API_URL}/api/intelligence/auth/logout`)
        .catch(err => { logger.warn('Logout request failed', err?.response?.status); })
        .finally(() => { pendingLogout.current = null; });
    }
    await pendingLogout.current;
  }, []);

  // Mirror the in-memory token to a Bearer header for components that still pass it explicitly.
  useEffect(() => {
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete axios.defaults.headers.common['Authorization'];
  }, [token]);

  // Restore session from the httpOnly cookie on mount.
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) { setLoading(false); return; }
    let cancelled = false;
    const epoch = authEpoch.current;
    (async () => {
      try {
        // Reuse the in-flight request during StrictMode's effect replay. An anonymous
        // visit still legitimately receives one 401; no redundant network probes.
        if (!sessionProbe.current) sessionProbe.current = axios.get(`${API_URL}/api/intelligence/auth/me`);
        const response = await sessionProbe.current;
        if (cancelled || signedOut.current || epoch !== authEpoch.current) return;
        const { token: sessionToken, ...userData } = response.data;
        setUser(userData);
        setToken(sessionToken || null);
      } catch (err) {
        if (!cancelled && err?.response?.status && err.response.status !== 401) logger.warn('Session restore failed', err.response.status);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signup = useCallback(async (email, password, name) => {
    await pendingLogout.current;
    signedOut.current = false;
    const epoch = ++authEpoch.current;
    const response = await axios.post(`${API_URL}/api/intelligence/auth/signup`, { email, password, name });
    if (epoch === authEpoch.current) applySession(response.data, setToken, setUser);
    return response.data;
  }, []);

  const login = useCallback(async (email, password) => {
    await pendingLogout.current;
    signedOut.current = false;
    const epoch = ++authEpoch.current;
    const response = await axios.post(`${API_URL}/api/intelligence/auth/login`, { email, password });
    if (epoch === authEpoch.current) applySession(response.data, setToken, setUser);
    return response.data;
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    await pendingLogout.current;
    signedOut.current = false;
    const epoch = ++authEpoch.current;
    const response = await axios.post(`${API_URL}/api/intelligence/auth/google/session`, { session_id: sessionId });
    if (epoch === authEpoch.current) applySession(response.data, setToken, setUser);
    return response.data;
  }, []);

  const refreshUser = useCallback(async () => {
    if (signedOut.current) return null;
    const epoch = authEpoch.current;
    const response = await axios.get(`${API_URL}/api/intelligence/auth/me`);
    const { token: sessionToken, ...userData } = response.data;
    if (!signedOut.current && epoch === authEpoch.current) {
      setUser(userData);
      if (sessionToken) setToken(sessionToken);
    }
    return userData;
  }, []);

  const updateUser = useCallback(next => {
    if (signedOut.current) return;
    setUser(current => {
      if (!current) return current;
      const updated = typeof next === 'function' ? next(current) : next;
      return updated?.id === current.id ? updated : current;
    });
  }, []);

  const state = useMemo(() => ({
    user, setUser: updateUser, token, loading,
    isAuthenticated: !!user,
    isPremium: user?.role === 'premium',
  }), [user, token, loading, updateUser]);

  const actions = useMemo(() => ({
    signup, login, loginWithGoogleSession, logout, refreshUser,
  }), [signup, login, loginWithGoogleSession, logout, refreshUser]);

  const value = useMemo(() => ({ ...state, ...actions }), [state, actions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthContext;
